/**
 * @module AuthContext
 * @description Authentifizierungs-Context fuer die gesamte Anwendung.
 * Verwaltet Login/Logout, Session-Pruefung via JWT-Cookie und
 * stellt den aktuellen Benutzer (user, rolle) per useAuth()-Hook bereit.
 *
 * @exports AuthProvider - Context-Provider (umschliesst die App)
 * @exports useAuth      - Hook: { user, loading, login, logout, refreshUser }
 * @usage Eingebunden in App.jsx; Zugriff per `const { user } = useAuth()`
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useTheme } from './ThemeContext';
import { geraetHeaders } from '../utils/kioskDevice';

const AuthContext = createContext(null);
const API = import.meta.env.VITE_API_URL || '';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // AuthProvider liegt innerhalb des ThemeProvider (main.jsx) — #1809 nutzt
  // bewusst den bestehenden setTheme-Weg statt eines zweiten Mechanismus.
  const { setTheme } = useTheme();

  const checkAuth = useCallback(async () => {
    try {
      // #1809-Fix: Der Device-Token entscheidet, ob der Server diese Sitzung als
      // Terminal bestaetigt. Er muss bei JEDEM /me mitgehen — nur so greift eine
      // Registrierung ohne Neuanmeldung und ein Widerruf beim naechsten Aufruf.
      const res = await fetch(`${API}/api/auth/me`, {
        credentials: 'include',
        headers: { ...geraetHeaders() },
      });
      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const res = await fetch(`${API}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Login fehlgeschlagen');
    // #1809: Ein persoenlicher Login beendet den Kiosk-Kontext dieses Tabs — sonst
    // bliebe das Flag stehen, das WorkLogin schon beim Betrachten gesetzt hat.
    sessionStorage.removeItem('weship_work_mode');
    setUser(data.user);
    return data.user;
  };

  const loginPin = async (barcode, pin) => {
    const res = await fetch(`${API}/api/portal/login-pin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...geraetHeaders() },
      credentials: 'include',
      body: JSON.stringify({ barcode, pin }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'PIN-Login fehlgeschlagen');
    setUser(data.user);
    return data.user;
  };

  const loginPersonalnummer = async (personalnummer, pin) => {
    const res = await fetch(`${API}/api/portal/login-personalnummer`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...geraetHeaders() },
      credentials: 'include',
      body: JSON.stringify({ personalnummer, pin }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'Login fehlgeschlagen');
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    await fetch(`${API}/api/auth/logout`, { method: 'POST', credentials: 'include' });
    sessionStorage.removeItem('weship_work_mode');
    // #1809 Shared-Device-Schutz: am geteilten Terminal darf der naechste
    // Mitarbeiter niemals das Theme des vorigen erben. Bewusst OHNE
    // syncToBackend — users.theme ist die persoenliche Vorliebe und muss den
    // Logout ueberleben, sonst setzt das Abmelden jeden auf hell zurueck.
    setTheme('light');
    setUser(null);
  };

  const updateProfile = async (data) => {
    const res = await fetch(`${API}/api/auth/profile`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    });
    const result = await res.json();
    if (!res.ok) throw new Error(result.error || 'Error');
    setUser(result.user);
    return result.user;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginPin, loginPersonalnummer, logout, checkAuth, updateProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
